import { useCallback, useEffect, useRef, useState } from 'react';

import {
  CONNECT_HANDOFF_GRACE_MS,
  HANDOFF_ANALYZING_DELAY_MS,
  connectHandoffPending,
  handoffPlaceholderKey,
  type HandoffPhase,
} from './connect-handoff';
import { useTranslation } from './i18n';

/**
 * Local busy state for the window after a connector connects, before the turn the genome starts is
 * visible on the conversation (see `lib/connect-handoff`).
 *
 * `noteConnected` is handed to the connector card, which calls it on the offered/waiting → connected
 * transition with the connector's display name. The flag expires on its own; a real turn taking over
 * (`remoteTurnActive`, a streamed THINKING row) simply outranks it at the render site, so nothing has
 * to clear it.
 *
 * `latestTurnOutputKey` is the newest assistant-side OUTPUT row — a reply, a step, or a card
 * (`latestTurnOutputKey(messages)`). The card that armed the handoff rides one of those, so it is present
 * the whole time the handoff is bridging — it cannot be what hides the loader. The flag instead ends the
 * moment a DIFFERENT output appears: that one is the turn the connect woke. It is deliberately BROADER than
 * "a reply with content" so a connect greeting that lands as a CARD (empty-`content` ASSISTANT row) or a
 * step still ends the bridge — otherwise the placeholder outlives the answer and renders beneath it.
 *
 * `handoffThinkingText` is the sequenced placeholder shown in the thinking line while the handoff
 * bridges: "Inspecting your {name}." immediately, then "Analyzing your data…" after
 * `HANDOFF_ANALYZING_DELAY_MS`. It resolves to `null` — so the indicator falls back to the generic
 * "Thinking…" — the moment the wait ends OR a real streamed thinking line arrives (`realThinkingActive`),
 * so it can never outlive the real thinking it stands in for.
 */
export function useConnectHandoff(
  latestTurnOutputKey: string | undefined,
  realThinkingActive = false,
): { pending: boolean; handoffThinkingText: string | null; noteConnected: (connectorName?: string) => void } {
  const { t } = useTranslation();
  const [armedAt, setArmedAt] = useState<number | null>(null);
  const [armedOutputKey, setArmedOutputKey] = useState<string | undefined>(undefined);
  const [phase, setPhase] = useState<HandoffPhase | null>(null);
  const [connectorName, setConnectorName] = useState<string | null>(null);

  const latestOutputKeyRef = useRef(latestTurnOutputKey);
  latestOutputKeyRef.current = latestTurnOutputKey;

  const noteConnected = useCallback((name?: string) => {
    setArmedAt(Date.now());
    setArmedOutputKey(latestOutputKeyRef.current);
    setConnectorName(name && name.trim() ? name : null);
    setPhase('inspecting');
  }, []);

  // Re-evaluate once at the horizon — the flag has no other end, so without this the indicator it
  // switched on would never switch off.
  useEffect(() => {
    if (armedAt === null) return undefined;
    const timer = setTimeout(() => setArmedAt(null), CONNECT_HANDOFF_GRACE_MS);
    return () => clearTimeout(timer);
  }, [armedAt]);

  const pending = connectHandoffPending(armedAt) && armedOutputKey === latestTurnOutputKey;

  // The 4s swap: "Inspecting your {name}." → "Analyzing your data…". Cancelled on cleanup and whenever
  // the phase is cleared below, so a wait that ends inside the window never swaps.
  useEffect(() => {
    if (phase !== 'inspecting') return undefined;
    const timer = setTimeout(() => setPhase('analyzing'), HANDOFF_ANALYZING_DELAY_MS);
    return () => clearTimeout(timer);
  }, [phase]);

  // The placeholder never outlives the real thinking, and never outlives the wait: drop it (and, via
  // the effect above, its pending swap timer) the moment a real THINKING line streams in or the pending
  // window closes.
  useEffect(() => {
    if ((!pending || realThinkingActive) && (phase !== null || connectorName !== null)) {
      setPhase(null);
      setConnectorName(null);
    }
  }, [pending, realThinkingActive, phase, connectorName]);

  const placeholderKey = handoffPlaceholderKey(phase, { pending, realThinkingActive, hasName: connectorName !== null });
  const handoffThinkingText =
    placeholderKey === 'chat.thinking.inspecting'
      ? t('chat.thinking.inspecting', { name: connectorName ?? '' })
      : placeholderKey === 'chat.thinking.analyzing'
        ? t('chat.thinking.analyzing')
        : null;

  return { pending, handoffThinkingText, noteConnected };
}
